import { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { DEFAULT_ARTICLE_CONFIG, normalizeArticlesConfig, buildArticlesEntityPrefixes } from './articlesConfigDefaults';

let hydratePromise = null;

function isArticlesConfigHydrated(articles) {
  return articles?._syncedFromApi === true;
}

function hydrateArticlesConfig(patchSettings) {
  if (!hydratePromise) {
    hydratePromise = localApiAdapter
      .get('/articles/config')
      .then((response) => {
        const raw = response?.data !== undefined ? response.data : response;
        const normalized = normalizeArticlesConfig(raw || {});
        syncArticlesConfigToSettings(normalized, patchSettings);
        return normalized;
      })
      .catch(() => {
        hydratePromise = null;
        return DEFAULT_ARTICLE_CONFIG;
      });
  }
  return hydratePromise;
}

/** Live articles module config — synced from API once, then from SettingsContext. */
export function useArticlesConfig() {
  const { settings, patchSettings } = useSettings();

  useEffect(() => {
    if (isArticlesConfigHydrated(settings?.articles)) return;
    void hydrateArticlesConfig(patchSettings);
  }, [settings?.articles, patchSettings]);

  return useMemo(
    () => normalizeArticlesConfig(settings?.articles || {}),
    [settings?.articles],
  );
}

export function syncArticlesConfigToSettings(config, patchSettings, existingSettings = {}) {
  const normalized = normalizeArticlesConfig(config);
  patchSettings({
    articles: { ...normalized, _syncedFromApi: true },
    configuration: {
      entityPrefixes: {
        ...(existingSettings?.configuration?.entityPrefixes || {}),
        ...buildArticlesEntityPrefixes(normalized),
      },
    },
  }, { persist: false });
  return normalized;
}

/** Sync table/cards toggle with articles config defaultView (real-time). */
export function useArticlesListViewMode(articlesConfig) {
  const [viewMode, setViewMode] = useState(() => articlesConfig?.defaultView || 'table');

  useEffect(() => {
    setViewMode(articlesConfig?.defaultView || 'table');
  }, [articlesConfig?.defaultView]);

  return [viewMode, setViewMode];
}
